import { apiClient } from '@leap4/frontend';
import { useEffect, useMemo, useState } from 'react';

import { reportingBaseUrl } from '../../../../config/generals';

export type ReportTypeBadge = 'impression' | 'conversion';

export interface IDimensionMeta {
    key: string;
    label: string;
    type: 'string' | 'integer' | 'boolean' | 'date' | 'datetime';
    category?: string;
    description?: string;
    reports?: ReportTypeBadge[];

    isTimestamp?: boolean;

    blockedWithServed?: boolean;
}

export interface IMetricMeta {
    key: string;
    label: string;
    aggregation?: string;
    format?: string;
    description?: string;
    reports?: ReportTypeBadge[];

    cheap?: boolean;

    rate?: boolean;

    selectable?: boolean;

    hint?: string;
}

export interface IReportingGrains {
    preaggregated: string[];
    servedRollup: string[];
}

export interface IReportingMetadata {
    dimensions: IDimensionMeta[];
    metrics: IMetricMeta[];
    grains: IReportingGrains;

    labels: Map<string, string>;
    loaded: boolean;
}

const EMPTY: IReportingMetadata = {
    dimensions: [],
    metrics: [],
    grains: { preaggregated: [], servedRollup: [] },
    labels: new Map(),
    loaded: false,
};

export const useReportingMetadata = (): IReportingMetadata => {
    const [dimensions, setDimensions] = useState<IDimensionMeta[]>([]);
    const [metrics, setMetrics] = useState<IMetricMeta[]>([]);
    const [grains, setGrains] = useState<IReportingGrains>(EMPTY.grains);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [dimRes, metRes] = await Promise.all([
                    apiClient.get<{ dimensions: IDimensionMeta[]; grains: IReportingGrains }>(
                        `${reportingBaseUrl}/dimensions`,
                    ),
                    apiClient.get<{ metrics: IMetricMeta[] }>(`${reportingBaseUrl}/metrics`),
                ]);
                if (cancelled) return;
                setDimensions(dimRes.data?.dimensions ?? []);
                setGrains(dimRes.data?.grains ?? EMPTY.grains);
                setMetrics(metRes.data?.metrics ?? []);
                setLoaded(true);
            } catch (err) {

                console.error('Failed to fetch reporting metadata:', err);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const labels = useMemo(() => {
        const map = new Map<string, string>();
        for (const d of dimensions) map.set(d.key, d.label);
        for (const m of metrics) map.set(m.key, m.label);
        return map;
    }, [dimensions, metrics]);

    return useMemo(
        () => ({ dimensions, metrics, grains, labels, loaded }),
        [dimensions, metrics, grains, labels, loaded],
    );
};
